/**
 * Common emeter (energy meter) monitoring capabilities
 * Used by Pro EM, Pro 3EM devices
 *
 * Emeters are monitoring-only devices with no control actions
 */

/**
 * Power consumption condition
 * @param {number} maxPower - Maximum power capacity in watts
 */
export function createPowerConsumptionCondition(maxPower = 7360) {
	return {
		name: 'Power Consumption',
		description: 'Check if power consumption meets a condition',
		parameters: {
			operator: {
				type: 'enum',
				required: true,
				values: ['greater_than', 'less_than', 'equal_to', 'between'],
				default: 'greater_than',
				description: 'Comparison operator',
			},
			threshold: {
				type: 'number',
				required: true,
				min: 0,
				max: maxPower,
				unit: 'watts',
				description: 'Power threshold value',
			},
			threshold_max: {
				type: 'number',
				required: false,
				min: 0,
				max: maxPower,
				unit: 'watts',
				description: 'Upper threshold (for "between" operator)',
			},
		},
		evaluateCondition: (device, channel, params) => {
			const currentPower = device.status?.[channel]?.apower || 0;
			const { operator, threshold, threshold_max } = params;

			switch (operator) {
				case 'greater_than':
					return currentPower > threshold;
				case 'less_than':
					return currentPower < threshold;
				case 'equal_to':
					return Math.abs(currentPower - threshold) < 1;
				case 'between':
					return currentPower >= threshold && currentPower <= (threshold_max || maxPower);
				default:
					return false;
			}
		},
	};
}

/**
 * Voltage level condition
 */
export function createVoltageLevelCondition(maxVoltage = 265) {
	return {
		name: 'Voltage Level',
		description: 'Check if voltage meets a condition',
		parameters: {
			operator: {
				type: 'enum',
				required: true,
				values: ['greater_than', 'less_than', 'equal_to', 'between'],
				default: 'greater_than',
				description: 'Comparison operator',
			},
			voltage: {
				type: 'number',
				required: true,
				min: 0,
				max: maxVoltage,
				unit: 'volts',
				description: 'Voltage threshold',
			},
			voltage_max: {
				type: 'number',
				required: false,
				min: 0,
				max: maxVoltage,
				unit: 'volts',
				description: 'Upper voltage threshold',
			},
		},
		evaluateCondition: (device, channel, params) => {
			const currentVoltage = device.status?.[channel]?.voltage || 0;
			const { operator, voltage, voltage_max } = params;

			switch (operator) {
				case 'greater_than':
					return currentVoltage > voltage;
				case 'less_than':
					return currentVoltage < voltage;
				case 'equal_to':
					return Math.abs(currentVoltage - voltage) < 0.1;
				case 'between':
					return currentVoltage >= voltage && currentVoltage <= (voltage_max || maxVoltage);
				default:
					return false;
			}
		},
	};
}

/**
 * Power threshold trigger
 */
export function createPowerThresholdTrigger(maxPower = 7360) {
	return {
		name: 'Power Threshold Crossed',
		description: 'Fires when power consumption crosses a threshold',
		parameters: {
			threshold: {
				type: 'number',
				required: true,
				min: 0,
				max: maxPower,
				unit: 'watts',
				description: 'Power threshold value',
			},
			direction: {
				type: 'enum',
				required: true,
				values: ['rising', 'falling', 'both'],
				default: 'both',
				description: 'Trigger direction',
			},
		},
		evaluateTrigger: (device, channel, previousStatus, currentStatus, params) => {
			const prevPower = previousStatus?.[channel]?.apower || 0;
			const currPower = currentStatus?.[channel]?.apower || 0;
			const { threshold, direction } = params;

			const wasBelow = prevPower < threshold;
			const isAbove = currPower >= threshold;

			if (direction === 'rising') {
				return wasBelow && isAbove;
			} else if (direction === 'falling') {
				return !wasBelow && !isAbove;
			} else {
				return (wasBelow && isAbove) || (!wasBelow && !isAbove);
			}
		},
	};
}
